import {useFocusManagement} from '@aboutyou/a11y';
import {useTranslation} from '@aboutyou/i18n';
import {Fade, Slide} from '@aboutyou/transitions';
import {styled} from '@linaria/react';
import React, {FC, PropsWithChildren, SyntheticEvent, useCallback} from 'react';
import {createPortal} from 'react-dom';
import {COLORS} from '../../../constants/colors';
import {tabletAndUpLinaria} from '../../../constants/customMedia';
import {Z_INDEXES} from '../../../constants/layers';
import {useKeyPress} from '../../../hooks/useKeyPress/useKeyPress';
import {useLockBodyScroll} from '../../../hooks/useLockBodyScroll/useLockBodyScroll';
import {useModalPortalElement} from '../../../hooks/usePortalElement/useModalPortalElement';
import {BlurredOverlay} from '../BlurredOverlay/BlurredOverlay';
import {HapticButton} from '../Buttons/HapticButton';
import {ContentPopperBaseProps} from '../ContentPopper/ContentPopper';
import {
  CONTENT_POPPER_ANIMATION_DURATION_MOUNT,
  CONTENT_POPPER_ANIMATION_DURATION_UNMOUNT,
} from '../ContentPopper/utils/contentPopperAnimationDurations';
import {Icon, IconSize} from '../Icon/Icon';
import {GenericIconName} from '../Icon/IconName';

export type ModalProps = {
  ['data-testid']?: string;
  bodyContent?: ContentPopperBaseProps['bodyContent'];
  onClose: ContentPopperBaseProps['onClose'];
  open: ContentPopperBaseProps['open'];
  onModalClick?: VoidFunction | React.MouseEventHandler;
  className?: string;
  hideCloseButton?: boolean;
  /**
   * use `renderInPortal` flag to render the modal in a separate portal element
   * This would be needed in some rare case where the rendered modal has z-index issues
   */
  renderInPortal?: boolean;
  onUnmount?: VoidFunction;
  animated?: boolean;
  zIndex?: Z_INDEXES;
  backgroundBlur?: boolean;
  preventSlideUpAnimation?: boolean;
  preventScrollLock?: boolean;
  CloseButtonComponent?: FC<CloseButtonProps>;
  disablePopoverStack?: boolean;
  popoverPriority?: number;
};

const disableOtherEvents = (e: SyntheticEvent) => {
  e.stopPropagation();
  e.preventDefault();
};

export const Modal: FC<ModalProps> = ({
  'data-testid': dataTestId = 'modal',
  bodyContent,
  hideCloseButton = false,
  onClose,
  onModalClick,
  renderInPortal = false,
  open,
  onUnmount,
  animated = true,
  backgroundBlur = false,
  zIndex = Z_INDEXES.MODAL_DIALOG_WRAPPER,
  preventSlideUpAnimation,
  preventScrollLock = false,
  CloseButtonComponent = CloseButton,
  popoverPriority,
  ...rest
}) => {
  const {focusManagementRefCallback, isTopMostPopover} = useFocusManagement({
    isOpen: open,
    priority: popoverPriority,
  });
  useKeyPress({
    key: 'Escape',
    onKeyDown: onClose,
    disabled: !open || isTopMostPopover === false,
  });
  useLockBodyScroll({lockScroll: preventScrollLock ? false : open});

  const onCloseModal = useCallback(
    (e: SyntheticEvent) => {
      disableOtherEvents(e);
      onClose?.();
    },
    [onClose],
  );

  const content = (
    <FadeAnimation open={open} animated={animated} onUnmount={onUnmount}>
      <BlurredOverlay
        onClick={onModalClick}
        onScroll={disableOtherEvents}
        aria-describedby="modalContent"
        aria-modal="true"
        data-testid={dataTestId}
        role="dialog"
        tabIndex={-1}
        $zIndex={zIndex}
        $enableBlur={backgroundBlur}
        {...rest}
      >
        <BodyAnimation
          open={open}
          animated={animated}
          bodyContent={
            <InnerWrapper>
              <FocusManagement ref={focusManagementRefCallback()} tabIndex={0}>
                {bodyContent}
                {!hideCloseButton && (
                  <CloseButtonComponent
                    onClick={onCloseModal}
                    data-testid={dataTestId}
                  />
                )}
              </FocusManagement>
            </InnerWrapper>
          }
          preventSlideUpAnimation={preventSlideUpAnimation}
        />
      </BlurredOverlay>
    </FadeAnimation>
  );

  if (renderInPortal) {
    return <RenderInPortal>{content}</RenderInPortal>;
  }

  return content;
};

const BodyAnimation: FC<
  Pick<
    ModalProps,
    'bodyContent' | 'animated' | 'preventSlideUpAnimation' | 'open'
  >
> = ({bodyContent, animated, preventSlideUpAnimation, open}) => {
  if (!animated || preventSlideUpAnimation) {
    return bodyContent;
  }

  return (
    <Slide mounted={open} direction="up" duration={300} transformLength={5}>
      {bodyContent}
    </Slide>
  );
};

const FadeAnimation: FC<
  PropsWithChildren<Pick<ModalProps, 'animated' | 'open' | 'onUnmount'>>
> = ({children, animated, open, onUnmount}) => {
  if (!animated) {
    return <>{children}</>;
  }

  return (
    <Fade
      mounted={open}
      duration={{
        mount: CONTENT_POPPER_ANIMATION_DURATION_MOUNT,
        unmount: CONTENT_POPPER_ANIMATION_DURATION_UNMOUNT,
      }}
      onUnmount={onUnmount}
    >
      {children}
    </Fade>
  );
};

const InnerWrapper = styled.div`
  height: 100%;
  width: 100%;
  overflow: hidden;

  ${tabletAndUpLinaria(`
    height: unset;
    width: unset;
  `)}
`;

const FocusManagement = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  ${tabletAndUpLinaria(`
    height: unset;
    width: unset;
  `)}
`;

interface CloseButtonProps {
  'data-testid'?: string;
  onClick?:
    | ((e: SyntheticEvent) => void)
    | ((e: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => void);
  className?: string;
  iconSize?: keyof typeof IconSize;
  iconColor?: COLORS;
}

const CloseButton: FC<CloseButtonProps> = ({
  'data-testid': dataTestid,
  onClick,
  className,
  iconSize = 'M',
  iconColor,
}) => {
  const {t} = useTranslation();

  return (
    <StyledButton
      data-testid={`${dataTestid}CloseIcon`}
      onClick={onClick}
      aria-label={t('Close')}
      className={className}
    >
      <Icon asset={GenericIconName.Close} size={iconSize} fill={iconColor} />
    </StyledButton>
  );
};

const RenderInPortal: React.FC<React.PropsWithChildren> = ({children}) => {
  const modalPortalElement = useModalPortalElement();

  if (!modalPortalElement) {
    return null;
  }

  return createPortal(<>{children}</>, modalPortalElement);
};

const StyledButton = styled(HapticButton)`
  position: absolute;
  top: 0;
  right: 0;
  padding: 24px;
`;
